import React, { createContext, useMemo, useState, useContext, ReactNode, useCallback } from 'react';
import { ThemeProvider, CssBaseline, PaletteMode } from '@mui/material';
import { createAppTheme } from './theme';
import {
  DEFAULT_NUMBER_SCALE_ID,
  getNumberScalePalette,
  NumberScaleId,
  NumberScalePalette,
} from './settings/numberScale';

const NUMBER_SCALE_STORAGE_KEY = 'reckonBoltNumberScale';

interface ThemeContextProps {
  toggleTheme: () => void;
  mode: PaletteMode;
  numberScaleId: NumberScaleId;
  numberScale: NumberScalePalette;
  setNumberScaleId: (id: NumberScaleId) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderComponentProps {
  children: ReactNode;
}

function readStoredScaleId(): NumberScaleId {
  try {
    return getNumberScalePalette(localStorage.getItem(NUMBER_SCALE_STORAGE_KEY)).id;
  } catch {
    return DEFAULT_NUMBER_SCALE_ID;
  }
}

export const ThemeProviderComponent: React.FC<ThemeProviderComponentProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>('dark');
  const [numberScaleId, setNumberScaleIdState] = useState<NumberScaleId>(readStoredScaleId);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const setNumberScaleId = useCallback((id: NumberScaleId) => {
    setNumberScaleIdState(id);
    try {
      localStorage.setItem(NUMBER_SCALE_STORAGE_KEY, id);
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const numberScale = useMemo(() => getNumberScalePalette(numberScaleId), [numberScaleId]);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode, numberScaleId, numberScale, setNumberScaleId }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
